import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  IconDatabase,
  IconFolder,
  IconGoGame,
  IconHistory,
  IconKey,
  IconLogs,
  IconPalette,
  IconSitemap,
  IconSTurnRight,
  IconTag,
  IconUserSquareRounded,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon } from '@rossum/ui/material';
import { useAccessibleFeatures } from '../../components/NavBar/hooks/useAccessibleFeatures';
import {
  settingsMenuItems,
  visibleMenuItemsForTab,
} from '../../components/NavBar/navigationStructure';
import { userRoleNameSelector } from '../../redux/modules/user/selectors';
import Tile from './components/Tile';
import { SettingsHeader } from './SettingsHeader';

const iconProps = {
  fontSize: 'xLarge',
  color: 'info',
} as const;

const tileConfig = {
  queueManager: {
    icon: IconFolder,
    titleKey: 'features.queueManager.title' as const,
    descriptionKey: 'features.queueManager.description' as const,
  },
  users: {
    icon: IconUserSquareRounded,
    titleKey: 'containers.settings.users.header.title' as const,
    descriptionKey: 'containers.settings.users.description' as const,
  },
  fieldManager: {
    icon: IconGoGame,
    titleKey: 'features.fieldManager.overview.ui.header.title' as const,
    descriptionKey: 'features.fieldManager.description' as const,
  },
  labels: {
    icon: IconTag,
    titleKey: 'containers.settings.labels.header.title' as const,
    descriptionKey: 'containers.settings.labels.description' as const,
  },
  datasets: {
    icon: IconDatabase,
    titleKey: 'features.datasets.tile.title' as const,
    descriptionKey: 'features.datasets.tile.text' as const,
  },
  ruleManager: {
    icon: IconSTurnRight,
    titleKey: 'features.ruleManager.list.title' as const,
    descriptionKey: 'features.ruleManager.tile.descriptionNew' as const,
  },
  activities: {
    icon: IconLogs,
    titleKey: 'containers.settings.activities.header.title' as const,
    descriptionKey: 'containers.settings.activities.description' as const,
  },
  configurationChangelog: {
    icon: IconHistory,
    titleKey: 'features.configurationChangelog.header.title' as const,
    descriptionKey: 'features.configurationChangelog.tile.description' as const,
  },
  workflows: {
    icon: IconSitemap,
    titleKey: 'features.workflows.settings.tile.title' as const,
    descriptionKey: 'features.workflows.settings.tile.description' as const,
  },
  credentials: {
    icon: IconKey,
    titleKey: 'features.credentials.tile.title' as const,
    descriptionKey: 'features.credentials.tile.description' as const,
  },
  appearance: {
    icon: IconPalette,
    titleKey: 'containers.settings.appearance.header.title' as const,
    descriptionKey:
      'containers.settings.appearance.header.description' as const,
  },
} satisfies Record<
  string,
  { icon: TablerIcon; titleKey: string; descriptionKey: string }
>;

const SettingsPage = () => {
  const intl = useIntl();
  const features = useAccessibleFeatures();
  const role = useSelector(userRoleNameSelector);
  const visibleItems = visibleMenuItemsForTab(
    settingsMenuItems,
    features,
    role
  );

  const tiles = visibleItems.flatMap(item => {
    const config = tileConfig[item.name as keyof typeof tileConfig];
    if (!config) return [];

    const Icon = config.icon;

    return [
      {
        id: item.dataCy,
        title: intl.formatMessage({ id: config.titleKey }),
        description: intl.formatMessage({ id: config.descriptionKey }),
        icon: (
          <SvgIcon {...iconProps}>
            <Icon />
          </SvgIcon>
        ),
        to: item.link,
        beta: item.beta,
        deprecated: item.deprecated,
      },
    ];
  });

  return (
    <PageLayout renderHeader={params => <SettingsHeader {...params} />}>
      <Stack px={4} py={4}>
        <Stack
          gap={4}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)' }}
        >
          {tiles.map(tile => (
            <Stack key={tile.title} sx={{ gridColumn: 'span 4' }}>
              <Tile {...tile} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </PageLayout>
  );
};

export default SettingsPage;
